import { useState, useEffect } from 'react';
import { NewsSource } from '../types';

interface SourceModalProps {
  source: NewsSource | null;
  onSave: (source: Omit<NewsSource, 'id' | 'lastFetchedAt'>) => Promise<void>;
  onClose: () => void;
}

export default function SourceModal({ source, onSave, onClose }: SourceModalProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [fetchInterval, setFetchInterval] = useState(60);
  const [contentWordLimit, setContentWordLimit] = useState<number | undefined>(undefined);
  const [promoted, setPromoted] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (source) {
      setName(source.name);
      setUrl(source.url);
      setImageUrl(source.imageUrl || '');
      setFetchInterval(source.fetchInterval);
      setContentWordLimit(source.contentWordLimit);
      setPromoted(source.promoted || false);
      setIsActive(source.isActive);
    }
  }, [source]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !url.trim()) {
      setError('Name and URL are required');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        url: url.trim(),
        imageUrl: imageUrl.trim() || null,
        fetchInterval,
        contentWordLimit,
        promoted,
        isActive,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save source');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{source ? 'Edit News Source' : 'Add News Source'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label required">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Forest News"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label required">RSS Feed URL</label>
                <input
                  type="url"
                  className="form-control"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/rss"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Image URL</label>
                <input
                  type="url"
                  className="form-control"
                  value={imageUrl}
                  onChange={e => setImageUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Fetch Interval (minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={fetchInterval}
                    onChange={e => setFetchInterval(Number(e.target.value))}
                    min="10"
                    step="10"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Content Word Limit</label>
                  <input
                    type="number"
                    className="form-control"
                    value={contentWordLimit || ''}
                    onChange={e =>
                      setContentWordLimit(e.target.value ? Number(e.target.value) : undefined)
                    }
                    placeholder="Leave empty for no limit"
                    min="10"
                    step="1"
                  />
                  <small className="form-hint">
                    Truncate article content to this many words (appends …)
                  </small>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={promoted}
                    onChange={e => setPromoted(e.target.checked)}
                  />
                  <span className="form-check-label">Promoted</span>
                  <small className="form-hint d-block">
                    Articles from this source will be prioritized
                  </small>
                </label>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                  <span className="form-check-label">Active</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  'Save Source'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
